'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import {
  Theme,
  getStoredTheme,
  setStoredTheme,
  getEffectiveTheme,
  applyTheme,
  watchSystemTheme,
} from '@/lib/theme';

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  mounted: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mounted, setMounted] = useState(false);
  const [theme, setThemeState] = useState<Theme>('system');
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  // Initialize theme on mount
  useEffect(() => {
    setMounted(true);

    const storedTheme = getStoredTheme();
    setThemeState(storedTheme);

    // Get the effective theme (system or stored)
    const effective = getEffectiveTheme();
    setEffectiveTheme(effective);

    // Apply the theme
    applyTheme(storedTheme);
  }, []);

  // Watch for system theme changes when using 'system' theme
  useEffect(() => {
    if (!mounted || theme !== 'system') return;

    const unsubscribe = watchSystemTheme((newSystemTheme) => {
      setEffectiveTheme(newSystemTheme);
      applyTheme('system');
    });

    return unsubscribe;
  }, [mounted, theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);
    setEffectiveTheme(getEffectiveTheme());
    applyTheme(newTheme);
  };

  const toggleTheme = () => {
    // Cycle through: system -> light -> dark -> system
    let newTheme: Theme;
    if (theme === 'system') {
      newTheme = 'light';
    } else if (theme === 'light') {
      newTheme = 'dark';
    } else {
      newTheme = 'system';
    }
    setTheme(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    effectiveTheme,
    setTheme,
    toggleTheme,
    mounted,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
